import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useUserProps } from "../../../util/userDBContext";
import DatePicker from "react-datepicker";
import moment from "moment";
import { Row, Col } from "react-bootstrap";
import Noti from "../../../util/noti";
import GYAxios from "../../../util/GYAxios";
import { checkIfArray, checkIfObjectHasKeys } from "../../../util/checking";
import { formatMonthlyData, calculatePvsystProd, calculateTargetProd } from "../../../util/targetCalc";
import { useSolarCalc } from "../../../util/calculations";
import MaterialTable from "material-table";
import { OMCOLS } from "./OMcolumn";
import summarytable from "./summarystyle.module.css";
import GeneralSummary from "./GeneralSummary";
import { isITUser, isManagementUser } from "../../../util/roles";
import { useUser } from "../../../util/user";
import { isValEmpty, isNaN } from "../../../util/formatter";

const SummaryReportOM = (props) => {
	const solarCalc = useSolarCalc();
	const user = useUser();
	const { loggedCountry } = useUserProps();

	// tabs: All, Thailand, Vietnam/ Makro P1/ Makro P2/ CP All
	const defaultCountry = useMemo(() => (isITUser(user.roles) || isManagementUser(user.roles)) ? "ALL" : loggedCountry, [loggedCountry, user.roles]);
	const [siteSet, setSiteSet] = useState(defaultCountry);
	const _siteSet = useRef(defaultCountry);
	const [clientBtn, setClientBtn] = useState("");

	const minDate = moment("2021-01-01", "YYYY-MM-DD").toDate();
	const maxDate = moment().subtract(1, "month").endOf("month").toDate();
	const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, "month"));

	const omCols = useMemo(() => OMCOLS, []);
	// require to re-render table
	const [omData, setOMData] = useState([]);
	const [omIsLoading, setOMIsLoading] = useState(true);
	const tableRef = useRef();
	const [generalData, setGeneralData] = useState(omData);

	const warningMsg = useCallback(() => {
		Noti.create({ type: "w", msg: "Please select a month", });
	}, []);

	/* const isValEmpty = (val) => val === null || val === undefined || val === "";
	const isNaN = (val) => isValEmpty(val) || Number.isNaN(val); */

	/**
	 * @type {Array}
	 */
	const getRequestData = async (uri) => {
		return await GYAxios.get(uri).then((res) => {
			try {
				if (res.data.error !== undefined) {
					console.log(res.data.error);
					return null;
				} else {
					if (checkIfArray(res.data)) return res.data;
					else {
						console.log(res.data.error);
						return null;
					}
				}
			} catch (e) {
				console.log(e);
				return null;
			}
		}).catch((err) => {
			console.log(err);
			return null;
		});
	};

	/**
 * Calculate difference between 2 values
 * @param {Actual value} actual
 * @param {Expected value} exp
 * @param {Irradiance values} isIrr
 * @returns
 */
	const calculateDiff = (actual, exp, isIrr) => {
		let res = "-";

		if (actual != null && exp != null && exp != 0 && exp != "-" && !isNaN(exp)) {
			if (isIrr) exp = exp * 6000;
			res = (((actual - exp) / exp) * 100).toFixed(2);
		}
		return res;
	};

	/**
	 * Calculate temperature difference between actual and target
	 * @param {number} targetTemp
	 * @param {obj} energyObj
	 * @returns Temperature difference
	 */
	const calculateDiffTemp = (targetTemp, energyObj) => {
		const hasCoeffModule = energyObj.coeffModule != 0;
		const diffTemp = hasCoeffModule
			? (energyObj.actualTemp - targetTemp) * energyObj.coeffModule
			: (energyObj.actualTemp - targetTemp) / targetTemp;
		return diffTemp;
	};

	const formatResEnergy = (energyObj, resParam, resUnused, resEgrid, resIrr, resTemp) => {
		// Selected Month and year
		const MMMM = selectedMonth.format("MMMM").toLowerCase();
		const YYYY = selectedMonth.format("YYYY");
		// Site details
		const tableName = energyObj.tableName;
		const capacity = energyObj.capacity;
		const contractDuration = energyObj.contractDuration;
		const CODobj = moment(energyObj.COD);
		const nowObj = moment();
		const formattedCOD = CODobj.format("YYYY-MM-DD");
		const accumMonths = selectedMonth.diff(CODobj, "months");
		const accumYears = parseInt(selectedMonth.diff(CODobj, "years"));
		// Target data
		const filteredParameter = resParam.filter((obj) => obj.table_name === tableName);
		const parameterObj = filteredParameter[0];
		if (!parameterObj) return null;
		// Correction percentages
		const GACorrections = parameterObj.GA_correction ?? 0;
		const TACorrections = parameterObj.TA_correction ?? 0;
		const otherCorrections = parameterObj.other_corrections ?? 0;
		const summedCorrections = GACorrections + TACorrections + otherCorrections;
		// Yearly degradation
		const degradation = parameterObj.degradation;
		// Get expected irradiance of selected month
		const filteredIrr = resIrr.filter((obj) => obj.table_name === tableName);
		const irrObj = filteredIrr[0];
		// Get expected temperature of selected month
		const filteredTemp = resTemp.filter((obj) => obj.table_name === tableName);
		const tempObj = filteredTemp[0];
		// Format unused data
		const filteredUnused = resUnused.filter((obj) => obj.table_name === tableName);
		const unusedObj = filteredUnused[0];
		// EGrid data
		const filteredEgrid = resEgrid.filter((obj) => obj.table_name === tableName);
		const egridObj = filteredEgrid[0];

		if (!parameterObj || !irrObj || !tempObj || !unusedObj || !egridObj) {
			console.warn(`[SummaryReportOM] missing target data for site: ${tableName}`);
			return null;
		}
		// Calculate monthly target production
		const targetProd = calculateTargetProd(egridObj, capacity, summedCorrections, unusedObj, degradation, formattedCOD, contractDuration);

		const egridData = Object.entries(formatMonthlyData(egridObj, "egrid"));
		const egridValues = egridData.map((egrid) => egrid[1]);
		const totalEGrid = egridValues.reduce((a, b) => a + b);

		// Asbuilt PVsyst Yield
		/* const asbuiltProd = solarCalc.parseFormatNumber((totalEGrid / capacity) * 1000); */
		const asbuiltProd = calculatePvsystProd(totalEGrid, capacity);

		//Net Yield including Unused
		const net_yield = parameterObj?.net_yield

		//TA loss (%) in Target Input
		const TA_loss = parameterObj?.TA_correction

		//GA loss (%) in Target Input
		const GA_loss = parameterObj?.GA_correction

		//Other losses (%) in Target Input
		const other_losses = parameterObj?.other_corrections

		const summartinfoneeded = { asbuiltProd, net_yield, TA_loss, GA_loss, other_losses, egridObj }

		// Get BP Target productions
		const BPtargetProd = targetProd[0];
		const allTargetProd = BPtargetProd.flat();
		// Get target production of selected month
		const selectedTarget = allTargetProd.filter((prod) => prod.month == MMMM && prod.year == YYYY);
		// Target values
		const targetExp = selectedTarget.length > 0 ? selectedTarget[0].monthlyProd : null;
		const targetIrr = irrObj[MMMM] != null ? irrObj[MMMM] : "-";
		const targetTemp = tempObj[MMMM] != null ? tempObj[MMMM] : "-";
		// Parameters to calculate indice
		const indiceParam = {
			actualProd: energyObj.actualProd,
			expProd: targetExp,
			actualIrr: solarCalc.calculateIrr(energyObj.actualIrr),
			expIrr: targetIrr,
		};
		// Parameters to calculate Expected PR
		const prParam = {
			prod: targetExp,
			irr: targetIrr != null ? targetIrr * 6000 : null,
			capacity,
		};
		// Values of parameters
		const prValues = Object.values(prParam);
		const indiceValues = Object.values(indiceParam);
		// Check if param contains null values
		const hasNullPRValues = prValues.includes(null);
		const hasNullIndiceValues = indiceValues.includes(null);
		// Calculated values
		const coeffOversize = energyObj.coeffOversize;
		const expProd = indiceParam.expProd;
		const coeffExpProd = expProd != null ? (coeffOversize / 100) * expProd : null;
		const expPR = !hasNullPRValues ? solarCalc.calculatePR(prParam) : null;
		const indice = !hasNullIndiceValues ? solarCalc.calculatePIndice(indiceParam) : null;
		// Temp diff calculation
		const hasValidtemp = targetTemp != null && targetTemp != 0 && energyObj.actualTemp != null;
		const diffTemp = hasValidtemp ? calculateDiffTemp(targetTemp, energyObj) : 0;
		const coefActualIrr = coeffExpProd != null
			? solarCalc.parseFormatNumber(coeffExpProd, 2) * solarCalc.parseFormatNumber(indiceParam.actualIrr, 2)
			: null;
		const coefExpIrr = coeffExpProd != null && targetIrr != null
			? solarCalc.parseFormatNumber(coeffExpProd, 2) *
			solarCalc.parseFormatNumber(targetIrr, 2)
			: null;
		// TA and GA values
		const TA = energyObj.TA;
		const GA = energyObj.gridAv;
		const TAprorate = coeffExpProd != null && TA != null ? coeffExpProd * (TA / 100) : null;
		const GAprorate = coeffExpProd != null && GA != null ? coeffExpProd * (GA / 100) : null;

		const egridThisMonth = egridObj[MMMM];
		const unusedThisMonth = unusedObj[MMMM];
		const _finalExpCor = (egridThisMonth - 100 * ( 1- TA_loss - GA_loss - other_losses - unusedThisMonth) /asbuiltProd) * net_yield;
		const finalExpCor = isNaN(_finalExpCor) ? 0 : Number(parseFloat(_finalExpCor).toFixed(0));
		const other_loss_PR = finalExpCor - energyObj.actualProd/1000;

		return {
			...energyObj,
			unusedObj,
			year: nowObj.format("YYYY"),
			COD: formattedCOD,
			coeffExpProd,
			accumMonths,
			accumYears,
			diffTemp,
			coefActualIrr,
			coefExpIrr,
			targetExp,
			targetIrr,
			targetTemp,
			expPR: isNaN(expPR) ? "" : expPR,
			indice,
			TAprorate: isNaN(TAprorate) ? "" : TAprorate,
			GAprorate: isNaN(GAprorate) ? "" : GAprorate,
			TA,
			GA: Number(parseFloat(GA).toFixed(2)),
			summartinfoneeded,
			finalExpCor,
			other_loss_PR,
		};
	};

	const processData = async () => {
		const endOfMonth = selectedMonth.endOf("month");
		const toDateParam = endOfMonth.format("YYYYMMDD");

		let column = "admin";
		let id = "all";

		let clientID = "";
		let country = "";

		switch (siteSet) {
			case "MK1":
			case "MK2":
				clientID = 18;
				country = "";
				break;
			case "CP":
				clientID = 21;
				country = "";
				break;
			default:
				country = siteSet;
				break;
		}

		const viewLevel = props.viewLevel;
		let reqMonthlyURI = "";
		let filterStr = "";

		const yearMonth = selectedMonth.startOf("month").format("YYYYMM");

		if (clientID != "") {
			column = "client_id";
			id = clientID;
			reqMonthlyURI = `/reports/summary/ops/filter/${column}/${id}/month/${yearMonth}/level/${viewLevel}`;
			filterStr = `filter?column=client_id&value=${id}`;
		} else {
			// Country selected
			const countryCol = "country";
			reqMonthlyURI = `/reports/summary/ops/filter/${countryCol}/${country}/month/${yearMonth}/level/${viewLevel}`;
			filterStr = `filter?column=country&value=${country}`;
		}

		const _site_cod = await getRequestData(`/site/cod/moment/${toDateParam}/filter/${column}/${id}?country=${country}`);
		const _monthly_site = await getRequestData(reqMonthlyURI);
		const _target_parameters = await getRequestData(`/target/parameters/${filterStr}`)
		const _target_unused = await getRequestData(`/target/unused/${filterStr}`);
		const _target_egrid = await getRequestData(`/target/egrid/${filterStr}`);
		const _target_irradiance = await getRequestData(`/target/irradiance/${filterStr}`);
		const _target_temperature = await getRequestData(`/target/temperature/${filterStr}`);
		const _free_nomoney_site = await getRequestData(`/site/free`);

		if (_site_cod === null || _monthly_site === null || _target_parameters === null || _target_unused === null || _target_egrid === null || _target_irradiance === null || _target_temperature === null || _free_nomoney_site === null) {
			Noti.create({ type: "w", msg: "Error fetching data" });
			return;
		}

		const _energy_site_monthly = await _monthly_site.map((_m_s) => formatResEnergy(
			_m_s,
			_target_parameters,
			_target_unused,
			_target_egrid,
			_target_irradiance,
			_target_temperature)
		).filter(Boolean);

		const free_site_name = _free_nomoney_site.map((site) => site.table_name);

		let _processed_sites = [];
		// Client selected
		if (clientID != "") {
			_site_cod.map((CODsite, id) => {
				for (let i = 0; i < _energy_site_monthly.length; i++) {
					const site = _energy_site_monthly[i];
					// Site is COD and not a free site
					if (CODsite.table_name === site.tableName && !free_site_name.includes(site.tableName)) {
						// Site is a Makro Site
						if (siteSet.includes("MK")) {
							// Site is a MK Phase 1 site
							if (siteSet === "MK1" && site.siteCode.length == 5)
								_processed_sites.push(renovateDataToNewStructure({ ...site, index: id, CODsite }));
							// Site is a MK Phase 2 site
							else if (siteSet === "MK2" && site.siteCode.length == 6)
								_processed_sites.push(renovateDataToNewStructure({ ...site, index: id, CODsite }));
						}
						// Site is a CP ALL site
						else _processed_sites.push(renovateDataToNewStructure({ ...site, index: id, CODsite }));
						break;
					}
				}
			});
		}
		// Country selected
		else {
			_site_cod.map((CODsite, id) => {
				for (let i = 0; i < _energy_site_monthly.length; i++) {
					const site = _energy_site_monthly[i];
					// Site is COD
					if (CODsite.table_name === site.tableName) {
						_processed_sites.push(renovateDataToNewStructure({ ...site, index: id, CODsite }));
						break;
					}
				}
			});
		}

		setOMData(_processed_sites);
		setGeneralData(_processed_sites);
	};

	/**
	 *
	 * @param {Object} data
	 * @returns Object
	 */
	const renovateDataToNewStructure = (data) => {
		const SF_project_id = data?.CODsite.SF_project_id;
		const actual_irradiation = solarCalc.calculateIrr(data.actualIrr);
		const expProd = isNaN(data.targetExp) ? "" : data.targetExp;
		const actProdVsExpProd = isNaN(expProd) ? data.actualProd : (data.actualProd - expProd);
		const theoEnergy = data.theoreticalPR / 100 * data.capacity * actual_irradiation;
		const siteScheme = data.CODsite.contract_id.includes("FIT") ? 2 : 1;
		const theoNgrVsActProd = theoEnergy - data.actualProd;
		const actualLGP_per = theoNgrVsActProd / theoEnergy * 100;
		const expUnusedByMonth = data.unusedObj[moment(selectedMonth).format("MMMM").toLowerCase()];
		const expMonUn = isNaN(expProd) ? "" : (expUnusedByMonth * expProd);
		const expMonUn_per = isNaN(expMonUn) ? "" : (expMonUn * 100 / theoEnergy);
		const actualProdLGP = data.actualProd + theoNgrVsActProd;
		const actualProdLGPvsExpProd = isNaN(expProd) ? actualProdLGP : (actualProdLGP - expProd); // as known as delta unused
		const actualProdLGPvsExpProd_per = isNaN(expProd) ? 100 : (expProd != 0 ? (actualProdLGPvsExpProd * 100 / expProd) : 100);
		const taGA = (data.TA * data.GA) / 100;
		const diffProd = isNaN(expProd) ? "" : Number(calculateDiff(data.actualProd, data.targetExp, false));
		const diffIrr = isNaN(expProd) ? "" : Number(calculateDiff(data.actualIrr, data.targetIrr, true));
		const perInx = isNaN(diffIrr) && isNaN(diffProd) ? "" : Number(diffIrr - diffProd);
		const perInxLGP = isNaN(perInx) ? "" : Number(perInx + actualLGP_per);
		const taGALoss = Number(100 - taGA);

		/* const TA_per_all = isNaN(expProd) ? 0 : TAprorate/ expProd;
		const GA_per_all = isNaN(expProd) ? 0 : GAprorate/ expProd; */
		const actual_TAGA_loss = isNaN(expProd) ? 0 : actualProdLGP / ((data.TAprorate * data.GAprorate) / Math.pow(expProd, 2)) - actualProdLGP;
		const delta_TAGA = isNaN(expProd) ? 0 : ((100 - data.TA) + (100 - data.GA)) * expProd - actual_TAGA_loss;
		const final_exp = expProd + delta_TAGA + actualProdLGPvsExpProd;

		return {
			...data,
			SF_project_id,
			actual_irradiation,
			expProd,
			actProdVsExpProd,
			diffProd,
			theoEnergy,
			expIrr: data.targetIrr,
			diffIrr,
			siteScheme,
			siteUnused: siteScheme === 1 ? theoNgrVsActProd : "",
			evnCurtailment: siteScheme === 2 ? theoNgrVsActProd : "",
			lgp: theoNgrVsActProd,
			actualLGP_per,
			expMonUn,
			expMonUn_per,
			actualProdLGP,
			actualProdLGPvsExpProd,
			actualProdLGPvsExpProd_per,
			taGA,
			moduleTemp: data.actualTemp,
			perInx,
			perInxLGP,
			taGALoss,
			final_exp
		};
	};

	const onSearchTableChange = (text) => {
		setOMIsLoading(true);
		const timer = setTimeout(() => {
			const _searchedData = tableRef.current.dataManager?.searchedData;
			setOMIsLoading(false);
			setGeneralData(_searchedData);
		}, 1000);
		return () => clearTimeout(timer);
	};

	useEffect(() => {
		setOMData([]);
		setGeneralData([]);
		processData();
	}, [props.viewLevel, selectedMonth]);

	useEffect(() => {
		if (siteSet !== _siteSet.current) {
			setOMData([]);
			setGeneralData([]);
			processData();
			_siteSet.current = siteSet;
		}
	}, [siteSet]);

	useEffect(() => {
		if (omData.length > 0) {
			setOMIsLoading(false);
		} else {
			setOMIsLoading(true);
		}
	}, [omData]);

	return <>
		<div className="panel-heading ">
			<Row style={{ marginBottom: "10px" }}>
				<Col md="12">
					<h2 className="panel-title" id="date">
						Summary table - O&M
					</h2>
				</Col>
			</Row>
			<Row>
				<Col md="4">
					<div className="month-selector">
						Choose another Month: &nbsp;&nbsp;
						<div style={{ display: "inline-block", width: '150px' }}>
						<DatePicker
							minDate={minDate}
							maxDate={maxDate}
							selected={selectedMonth.toDate()}
							onChange={(date) => {
								setSelectedMonth(moment(date));
							}}
							dateFormat="MMM yyyy"
							showMonthYearPicker
						/>
						</div>
					</div>
				</Col>
				<Col md="8" style={{ textAlign: "right" }}>
					<div id="summary-btns">
						<button
							disabled={siteSet === "ALL"}
							className={"btn btn-primary"}
							onClick={() => {
								if (siteSet !== "ALL") setSiteSet("ALL");
							}}
						>All</button>
						<button
							disabled={siteSet === "TH"}
							className={"btn btn-primary"}
							onClick={() => {
								if (siteSet !== "TH") setSiteSet("TH");
							}}
						>Thailand</button>

						<button
							disabled={siteSet === "VN"}
							className={"btn btn-primary"}
							onClick={() => {
								if (siteSet !== "VN") setSiteSet("VN");
							}}
						>Vietnam</button>

						<button
							disabled={siteSet === "KH"}
							className={"btn btn-primary"}
							onClick={() => {
								if (siteSet !== "KH") setSiteSet("KH");
							}}
						>Cambodia</button>

						<button
							disabled={siteSet === "PH"}
							className={"btn btn-primary"}
							onClick={() => {
								if (siteSet !== "PH") setSiteSet("PH");
							}}
						>Philippines</button>

						<button
							disabled={siteSet === "MK1"}
							className={"btn btn-primary"}
							onClick={() => {
								if (siteSet !== "MK1" && !clientBtn.includes("MK1")) setSiteSet("MK1");
								if (clientBtn.includes("MK1")) warningMsg();
							}}
						>Makro P1</button>

						<button
							disabled={siteSet === "MK2"}
							className={"btn btn-primary"}
							onClick={() => {
								if (siteSet !== "MK2" && !clientBtn.includes("MK2")) setSiteSet("MK2");
								if (clientBtn.includes("MK2")) warningMsg();
							}}
						>Makro P2</button>

						<button
							disabled={siteSet === "CP"}
							className={"btn btn-primary"}
							onClick={() => {
								if (siteSet !== "CP" && !clientBtn.includes("CP")) setSiteSet("CP");
								if (clientBtn.includes("CP")) warningMsg();
							}}
						>CP All</button>
					</div>
				</Col>
			</Row>
		</div>

		<div className="panel-body">
			<GeneralSummary data={generalData} />
		</div>
		<div className="panel-body">
			<MaterialTable
				columns={omCols}
				data={omData}
				isLoading={omIsLoading}
				style={summarytable}
				tableRef={tableRef}
				onSearchChange={(text) => onSearchTableChange(text)}
				options={{
					showTitle: false,
					exportButton: true,
					exportAllData: true,
					grouping: true,
					pageSize: 20,
					pageSizeOptions: [20, 50, 100, isNaN(omData.length) ? 150 : omData.length],
					headerStyle: {
						fontSize: "1rem",
						textAlign: "center",
					},
					cellStyle: { textAlign: "center", },
					fixedColumns: { left: 2, },
					emptyRowsWhenPaging: false,
					exportFileName: `SummRepOps-${selectedMonth.format("MMMM YYYY")}-${siteSet}`,
					filtering: false,
				}}
			/>
		</div>
	</>;
};

export default React.memo(SummaryReportOM);